import SellingUnit from "@/components/SellingUnit";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Minus, Plus, ShoppingCart, Truck, MapPin, ShieldCheck, Star } from "lucide-react";
import { useLang, useL } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { api, money, apiError } from "@/lib/api";
import { Stars, ProductCard } from "@/components/shared";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const L = useL();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState("description");
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });

  const load = useCallback(() => api.get(`/products/${slug}`).then((r) => { setData(r.data); setActiveImg(0); }).catch(() => setData(false)), [slug]);
  useEffect(() => { setData(null); load(); }, [load]);

  if (data === null) return <div className="grid min-h-[60vh] place-items-center text-navy">{t("common.loading")}</div>;
  if (data === false) return <div className="grid min-h-[60vh] place-items-center text-navy">{t("common.notFound")}. <Link to="/shop" className="ml-2 text-sunset underline">{t("nav.shop")}</Link></div>;

  const p = data.product;
  const onSale = p.sale_price && p.sale_price < p.price;
  const price = onSale ? p.sale_price : p.price;
  const soldOut = p.stock <= 0;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    try {
      await api.post(`/products/${p.id}/reviews`, reviewForm);
      toast.success(lang === "fr" ? "Avis publié !" : "Review posted!");
      setReviewForm({ rating: 5, comment: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail, lang)); }
  };

  return (
    <div data-testid="product-detail-page">
      <div className="mx-auto max-w-[1400px] px-4 py-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-sunset">{t("nav.home")}</Link> / <Link to="/shop" className="hover:text-sunset">{t("nav.shop")}</Link> / <span className="text-navy">{L(p, "name")}</span>
      </div>

      <div className="mx-auto grid max-w-[1400px] gap-10 px-4 pb-10 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <div className="overflow-hidden rounded-3xl border border-border bg-cream-card card-shadow">
            <img src={p.images?.[activeImg]} alt={L(p, "name")} className="aspect-square w-full object-cover" data-testid="product-main-image" />
          </div>
          {p.images?.length > 1 && (
            <div className="mt-4 flex gap-3">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-20 overflow-hidden rounded-xl border-2 transition ${activeImg === i ? "border-gold" : "border-transparent"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="mb-2 flex flex-wrap gap-2">
            {p.made_in_haiti && <span className="rounded-full bg-navy px-3 py-1 text-xs font-bold text-gold">100% Ayiti · Made in Haiti</span>}
            {onSale && <span className="rounded-full bg-sunset px-3 py-1 text-xs font-bold text-white">{t("common.sale")}</span>}
          </div>
          <h1 className="font-display text-3xl font-bold text-navy sm:text-4xl">{L(p, "name")}</h1>
          <SellingUnit product={p} explain />
          <div className="mt-3"><Stars value={p.rating} count={p.review_count} size={16} /></div>

          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-head text-3xl font-bold text-navy">{money(price)}</span>
            {onSale && <span className="text-lg text-muted-foreground line-through">{money(p.price)}</span>}
          </div>

          <p className="mt-4 leading-relaxed text-navy/70">{L(p, "description")}</p>

          <div className="mt-5 flex items-center gap-2 text-sm">
            <span className={`h-2.5 w-2.5 rounded-full ${soldOut ? "bg-destructive" : "bg-palm-500"}`} />
            <span className={soldOut ? "text-destructive" : "text-palm-500 font-semibold"}>{soldOut ? t("common.outOfStock") : `${t("common.inStock")} (${p.stock})`}</span>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-3 rounded-full border border-border bg-cream-card px-2 py-1">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-9 w-9 place-items-center rounded-full hover:bg-cream" aria-label="decrease" data-testid="qty-decrease"><Minus size={16} /></button>
              <span className="w-6 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-cream" aria-label="increase" data-testid="qty-increase"><Plus size={16} /></button>
            </div>
            <button disabled={soldOut} onClick={() => { addItem(p, qty); toast.success(t("common.addToCart")); }} data-testid="detail-add-to-cart"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition ${soldOut ? "cursor-not-allowed bg-muted text-muted-foreground" : "btn-gold"}`}>
              <ShoppingCart size={17} /> {t("common.addToCart")}
            </button>
            <button onClick={() => toggle(p.id)} className="grid h-12 w-12 place-items-center rounded-full border border-border bg-cream-card transition hover:border-sunset" aria-label="wishlist" data-testid="detail-wishlist">
              <Heart size={18} className={has(p.id) ? "fill-sunset text-sunset" : "text-navy"} />
            </button>
          </div>
          <button disabled={soldOut} onClick={() => { addItem(p, qty); navigate("/checkout"); }} data-testid="buy-now-btn"
            className={`mt-3 w-full rounded-full py-3.5 text-sm font-bold transition ${soldOut ? "cursor-not-allowed bg-muted text-muted-foreground" : "btn-sunset"}`}>
            {t("common.buyNow")}
          </button>

          <div className="mt-6 grid gap-3 rounded-2xl border border-border bg-cream-card p-5 text-sm">
            <div className="flex items-center gap-3 text-navy/80"><Truck size={17} className="text-gold" /> {lang === "fr" ? "Livraison fiable à travers les États-Unis" : "Reliable delivery across the United States"}</div>
            <div className="flex items-center gap-3 text-navy/80"><MapPin size={17} className="text-gold" /> {t("common.origin")}: {p.origin || "Ayiti"}</div>
            <div className="flex items-center gap-3 text-navy/80"><ShieldCheck size={17} className="text-gold" /> {lang === "fr" ? "Paiement sécurisé" : "Secure payment"}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mx-auto max-w-[1400px] px-4 py-6">
        <div className="mb-5 flex gap-2 border-b border-border">
          {[["description", t("common.description")], ["specs", t("common.specifications")], ["reviews", `${t("common.reviews")} (${data.reviews.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`border-b-2 px-4 py-3 text-sm font-semibold transition ${tab === k ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-navy"}`}>{label}</button>
          ))}
        </div>

        {tab === "description" && (
          <div className="max-w-3xl space-y-3 text-navy/75">
            <p className="leading-relaxed">{L(p, "description")}</p>
            {p.ingredients_fr && lang === "fr" && <p><strong className="text-navy">{t("common.ingredients")}:</strong> {p.ingredients_fr}</p>}
          </div>
        )}
        {tab === "specs" && (
          <div className="max-w-lg divide-y divide-border rounded-2xl border border-border bg-cream-card">
            {[[t("common.origin"), p.origin], ...(p.selling_unit === "marmite" ? [] : [[t("common.weight"), p.weight || "—"]]), ["SKU", p.sku], [t("common.category"), p.category_slug]].map(([k, v], i) => (
              <div key={i} className="flex justify-between px-5 py-3 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-navy">{v}</span></div>
            ))}
          </div>
        )}
        {tab === "reviews" && (
          <div className="max-w-3xl">
            <form onSubmit={submitReview} className="mb-6 rounded-2xl border border-border bg-cream-card p-5" data-testid="review-form">
              <p className="mb-2 font-head font-semibold text-navy">{lang === "fr" ? "Laisser un avis" : "Leave a review"}</p>
              <div className="mb-3 flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: i }))} aria-label={`${i} stars`}>
                    <Star size={22} className={i <= reviewForm.rating ? "fill-gold text-gold" : "text-navy/20"} />
                  </button>
                ))}
              </div>
              <textarea value={reviewForm.comment} onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))} placeholder={lang === "fr" ? "Votre avis..." : "Your review..."} rows={3}
                className="w-full rounded-xl border border-border bg-cream p-3 text-sm outline-none focus:border-gold" data-testid="review-comment" />
              <button type="submit" className="btn-gold mt-3 rounded-full px-6 py-2.5 text-sm font-semibold" data-testid="review-submit">{user ? t("common.submit") : t("nav.login")}</button>
            </form>
            {data.reviews.length === 0 ? <p className="text-muted-foreground">{lang === "fr" ? "Aucun avis pour le moment." : "No reviews yet."}</p> : (
              <div className="space-y-4">
                {data.reviews.map((r) => (
                  <div key={r.id} className="rounded-xl border border-border bg-cream-card p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-head text-sm font-semibold text-navy">{r.user_name}</span>
                      <Stars value={r.rating} />
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-navy/70">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Related */}
      {data.related.length > 0 && (
        <div className="mx-auto max-w-[1400px] px-4 py-10">
          <h2 className="mb-6 font-display text-2xl font-bold text-navy">{t("common.related")}</h2>
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {data.related.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </div>
      )}
    </div>
  );
}
